import { motion, AnimatePresence } from "framer-motion";
import { useCallback, useEffect, useState } from 'react';
import Confetti from "react-confetti";
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { FaFistRaised } from "react-icons/fa";
import styled from "styled-components";
import { socket } from '../../socket';
import BattleLayout from "./BattleLayout";
import Countdown from "./Countdown";
import TeamLayout from "./TeamLayout";
import './battle-log.css';
import './character-switch.css';
import './defeated-message.css';
import './switch-animation.css';
import './switch-prompt.css';

/** Current HP for battle UI (server sends modifiedHealth; some payloads use currentHealth). */
function getCharacterCurrentHp(c) {
    if (!c) return 0;
    if (c.modifiedHealth != null && !Number.isNaN(Number(c.modifiedHealth))) {
        return Number(c.modifiedHealth);
    }
    if (c.currentHealth != null && !Number.isNaN(Number(c.currentHealth))) {
        return Number(c.currentHealth);
    }
    if (c.health != null && !Number.isNaN(Number(c.health))) {
        return Number(c.health);
    }
    return 0;
}

function isCharacterDefeatedState(c) {
    if (!c) return true;
    if (c.isFainted) return true;
    return getCharacterCurrentHp(c) <= 0;
}

function sameCharacter(a, b) {
    if (!a || !b) return false;
    if (a._id != null && b._id != null && String(a._id) === String(b._id)) {
        return true;
    }
    if (a.id != null && b.id != null && String(a.id) === String(b.id)) {
        return true;
    }
    return a.name != null && a.name === b.name;
}

function findCharacterIndexInTeam(team, ch) {
    if (!Array.isArray(team) || !ch) return -1;
    const byId = team.findIndex(
        t => t && (String(t._id) === String(ch._id) || (t.id != null && ch.id != null && String(t.id) === String(ch.id)))
    );
    if (byId >= 0) return byId;
    return team.findIndex(t => t && t.name && t.name === ch.name);
}

/** Any opponent character data that appears in play must count as revealed for the team column. */
function markOpponentRevealed(c) {
    if (!c) return c;
    return { ...c, isRevealed: true };
}

/* ─────────────── Arena chrome — mass-poster design system ───────────────
   Canvas: deep wine red (red-900 → red-950 vignette), with film grain.
   Panels: ink, cut-corner, amber accent for active/primary.
   No purple. No animated rainbow gradients. No backdrop-blur purple.
----------------------------------------------------------------------- */
const GameContainer = styled(Container)`
  --grain-opacity: 0.12;
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  padding: var(--space-3);
  position: relative;
  color: var(--color-cream-200);
  font-family: var(--font-body);

  background:
    radial-gradient(ellipse at center, var(--color-red-800) 0%, var(--color-red-900) 55%, var(--color-red-950) 100%);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 50% 100%, rgba(8, 7, 10, 0.45) 0%, transparent 55%),
      radial-gradient(circle at 0% 0%, rgba(8, 7, 10, 0.35) 0%, transparent 45%),
      radial-gradient(circle at 100% 0%, rgba(8, 7, 10, 0.35) 0%, transparent 45%);
    pointer-events: none;
    z-index: 0;
  }
`;

const GameFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: var(--space-3) var(--space-5);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-4);
  z-index: 100;

  background:
    linear-gradient(180deg, rgba(8, 7, 10, 0.55) 0%, var(--color-ink-950) 100%);
  border-top: 1px solid var(--color-amber-500);
  box-shadow: 0 -8px 24px rgba(8, 7, 10, 0.55);
`;

const StatusMessage = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  font-family: var(--font-heading);
  font-size: var(--size-sm);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-wider);
  text-transform: uppercase;
  color: var(--color-cream-100);
  background: rgba(8, 7, 10, 0.55);
  border-left: 3px solid var(--color-amber-500);
  clip-path: var(--clip-button);
`;

const ForfeitButton = styled.button`
  appearance: none;
  background: var(--color-red-500);
  color: var(--color-cream-100);
  border: none;
  padding: 10px 22px;
  font-family: var(--font-heading);
  font-size: var(--size-xs);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  transition: background var(--dur-fast) var(--ease-out),
              transform var(--dur-fast) var(--ease-out);
  clip-path: var(--clip-button);

  &:hover:not(:disabled) {
    background: var(--color-red-400);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const HomeButton = styled.button`
  appearance: none;
  background: var(--color-amber-500);
  color: var(--color-ink-950);
  border: none;
  padding: 10px 22px;
  font-family: var(--font-heading);
  font-size: var(--size-xs);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  transition: background var(--dur-fast) var(--ease-out),
              transform var(--dur-fast) var(--ease-out),
              box-shadow var(--dur-fast) var(--ease-out);
  clip-path: var(--clip-button);

  &:hover {
    background: var(--color-amber-400);
    transform: translateY(-1px);
    box-shadow: var(--shadow-glow-amber);
  }
`;

const ReopenResultButton = styled.button`
  appearance: none;
  background: transparent;
  color: var(--color-cream-200);
  border: 1px solid var(--color-ink-500);
  padding: 8px 16px;
  font-family: var(--font-heading);
  font-size: var(--size-xs);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-wider);
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color var(--dur-fast) var(--ease-out),
              color var(--dur-fast) var(--ease-out);

  &:hover {
    border-color: var(--color-amber-500);
    color: var(--color-amber-500);
  }
`;

const ForfeitConfirmGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: 6px 8px 6px 14px;
  background: var(--color-ink-900);
  border: 1px solid var(--color-red-500);
  color: var(--color-cream-100);
  font-family: var(--font-body);
  font-size: var(--size-xs);
  clip-path: var(--clip-button);

  button {
    border: none;
    padding: 6px 14px;
    font-family: var(--font-heading);
    font-weight: var(--weight-bold);
    font-size: var(--size-2xs);
    letter-spacing: var(--tracking-widest);
    text-transform: uppercase;
    cursor: pointer;
    clip-path: var(--clip-button);
    transition: background var(--dur-fast) var(--ease-out);
  }

  .confirm-yes {
    background: var(--color-red-500);
    color: var(--color-cream-100);
    &:hover { background: var(--color-red-400); }
  }

  .confirm-no {
    background: var(--color-ink-700);
    color: var(--color-cream-200);
    &:hover { background: var(--color-ink-600); }
  }
`;

const TeamColumn = styled(Col)`
  padding: var(--space-2);
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  min-height: 0;

  .user-team-column, .opponent-team-column {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding: var(--space-3);
    background: var(--color-ink-900);
    border: 1px solid var(--color-ink-700);
    clip-path: var(--clip-card);
    box-shadow: var(--shadow-lg);
  }

  .user-team-column {
    border-left: 3px solid var(--color-amber-500);
  }

  .opponent-team-column {
    border-right: 3px solid var(--color-red-500);
  }
`;

const BattleColumn = styled(Col)`
  padding: var(--space-2);
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 2;
`;

// Game End Modal styled components
/* ─── Game-end poster-card — victory is amber, defeat is wine-red ─── */
const GameEndOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(8, 7, 10, 0.92);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;

  /* Halftone field — readable even over the blur so the overlay feels like
     a poster background, not a plain dim. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      radial-gradient(circle, rgba(255, 177, 60, 0.05) 1px, transparent 1px);
    background-size: 7px 7px;
    pointer-events: none;
  }
`;

const GameEndModal = styled(motion.div)`
  position: relative;
  background:
    linear-gradient(180deg, var(--color-ink-900) 0%, var(--color-ink-950) 100%);
  border: 1px solid ${p => p.$victory ? 'var(--color-amber-500)' : 'var(--color-red-500)'};
  clip-path: var(--clip-card);
  padding: 0;
  max-width: 520px;
  width: 92%;
  color: var(--color-cream-100);
  box-shadow:
    var(--shadow-lg),
    0 0 120px ${p => p.$victory ? 'rgba(255, 177, 60, 0.18)' : 'rgba(193, 39, 45, 0.18)'};
  overflow: hidden;

  /* Top edge — amber for victory, red for defeat */
  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 4px;
    background: ${p => p.$victory ? 'var(--color-amber-500)' : 'var(--color-red-500)'};
    z-index: 2;
  }
`;

const ModalHeader = styled.div`
  position: relative;
  padding: 40px 36px 24px;
  text-align: center;
  background: ${p => p.$victory
    ? 'radial-gradient(ellipse at 50% 0%, rgba(255, 177, 60, 0.16) 0%, transparent 60%)'
    : 'radial-gradient(ellipse at 50% 0%, rgba(193, 39, 45, 0.18) 0%, transparent 60%)'
  };
  border-bottom: 1px solid var(--color-ink-700);
`;

const GameEndIcon = styled(motion.div)`
  font-size: 2.5rem;
  margin-bottom: 10px;
  filter: drop-shadow(0 4px 14px ${p =>
      p.$victory ? 'rgba(255, 177, 60, 0.55)' : 'rgba(193, 39, 45, 0.55)'});
`;

/* Display-font italic with side slashes — pure fight-poster energy */
const GameEndTitle = styled(motion.h2)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin: 0 0 8px;
  font-family: var(--font-display);
  font-size: clamp(44px, 6vw, 68px);
  font-weight: var(--weight-bold);
  font-style: italic;
  letter-spacing: -0.01em;
  line-height: 0.95;
  text-transform: uppercase;
  color: ${p => p.$victory ? 'var(--color-amber-500)' : 'var(--color-red-500)'};
  text-shadow:
    0 3px 0 var(--color-ink-950),
    0 0 36px ${p => p.$victory ? 'rgba(255, 177, 60, 0.4)' : 'rgba(193, 39, 45, 0.4)'};

  &::before, &::after {
    content: '////';
    font-size: 0.4em;
    letter-spacing: -0.08em;
    opacity: 0.75;
  }
`;

const GameEndSubtitle = styled(motion.p)`
  margin: 4px 0 0;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.04em;
  color: var(--color-cream-300);
`;

const ModalBody = styled.div`
  padding: 22px 32px 10px;
`;

const ResultCard = styled(motion.div)`
  padding: 14px 16px;
  background: rgba(8, 7, 10, 0.5);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-card);
`;

const ResultRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 6px 0;

  &:not(:last-child) {
    border-bottom: 1px solid var(--color-ink-700);
  }
`;

const ResultLabel = styled.span`
  font-family: var(--font-heading);
  font-size: 10px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-cream-400);
`;

const ResultValue = styled.span`
  font-family: var(--font-heading);
  font-size: 15px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: ${p => p.$highlight ? 'var(--color-amber-500)' : 'var(--color-cream-100)'};
`;

const RatingSection = styled(motion.div)`
  margin-top: 14px;
  padding: 16px 18px;
  background: ${p => p.$positive
    ? 'rgba(103, 201, 122, 0.08)'
    : 'rgba(193, 39, 45, 0.08)'
  };
  border: 1px solid ${p => p.$positive
    ? 'rgba(103, 201, 122, 0.3)'
    : 'rgba(193, 39, 45, 0.35)'
  };
  clip-path: var(--clip-card);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
`;

const RatingNumber = styled.div`
  font-family: var(--font-display);
  font-size: 2.2rem;
  font-weight: var(--weight-bold);
  font-style: italic;
  color: var(--color-cream-100);
  line-height: 1;

  small {
    display: block;
    font-family: var(--font-heading);
    font-style: normal;
    font-size: 9px;
    font-weight: var(--weight-bold);
    color: var(--color-cream-400);
    text-transform: uppercase;
    letter-spacing: var(--tracking-widest);
    margin-top: 6px;
  }
`;

const RatingDelta = styled(motion.div)`
  font-family: var(--font-display);
  font-size: 1.6rem;
  font-weight: var(--weight-bold);
  font-style: italic;
  color: ${p => p.$positive ? 'var(--color-success)' : 'var(--color-red-500)'};
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${p => p.$positive
    ? 'rgba(103, 201, 122, 0.12)'
    : 'rgba(193, 39, 45, 0.12)'
  };
  border: 1px solid currentColor;
  clip-path: var(--clip-button);
`;

const ModalFooter = styled.div`
  padding: 22px 32px 28px;
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
`;

const GameEndButton = styled(motion.button)`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  padding: 11px 20px;
  font-family: var(--font-heading);
  font-size: 12px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  clip-path: var(--clip-button);
  transition: filter var(--dur-fast) var(--ease-out),
              transform var(--dur-fast) var(--ease-out);

  &:hover { filter: brightness(1.1); }
  &:active { transform: translateY(1px); }
`;

const PrimaryButton = styled(GameEndButton)`
  background: ${p => p.$victory ? 'var(--color-amber-500)' : 'var(--color-red-500)'};
  color: ${p => p.$victory ? 'var(--color-ink-950)' : 'var(--color-cream-100)'};
  box-shadow: var(--shadow-md);
`;

const SecondaryButton = styled(GameEndButton)`
  background: var(--color-ink-800);
  color: var(--color-cream-200);
  border: 1px solid var(--color-ink-600) !important;

  &:hover { background: var(--color-ink-700); }
`;


const GameLayout = (props) => {
    console.log(props)
    const [userTeam, setUserTeam] = useState(props.gameData?.userTeam);
    const [opponentTeam, setOpponentTeam] = useState(() => {
        const t = props.gameData?.opponent?.team;
        if (!Array.isArray(t)) {
            return [];
        }
        return t.map((c, i) =>
            i === 0 ? markOpponentRevealed(c) : { ...c, isRevealed: c.isRevealed === true }
        );
    });
    const [userSelectedCharecter, setUserSelectedCharecter] = useState(userTeam[0]);
    const [opponentSelectedCharecter, setOpponentSelectedCharceter] = useState(() =>
        markOpponentRevealed(props.gameData?.opponent?.selectedCharecter)
    );
    const [currentState, setCurrentState] = useState({ move: 'Select a move' })
    const [isPaused, setIsPaused] = useState(false);
    // needsSwitch tracks whether the user needs to switch characters (when current character faints)
    const [needsSwitch, setNeedsSwitch] = useState(false);
    // Tracks if we're waiting for the opponent to pick their next character after a KO
    const [waitingForOpponentSwitch, setWaitingForOpponentSwitch] = useState(false);
    // Game end state for displaying victory/defeat
    const [gameEndResult, setGameEndResult] = useState(null);
    // Once the match ends (natural KO or forfeit), keep the board locked even
    // if the user dismisses the end-of-match modal - otherwise they could
    // keep clicking moves on a finished game.
    const [gameEnded, setGameEnded] = useState(false);
    // Preserves the end-of-match payload so the player can reopen the modal
    // after dismissing it (e.g. to re-check the rating delta).
    const [lastGameEndResult, setLastGameEndResult] = useState(null);
    /** `game_end` is staged until move GIFs finish + dwell, so the last hit is visible. */
    const [stagedGameEnd, setStagedGameEnd] = useState(null);
    const [confirmingForfeit, setConfirmingForfeit] = useState(false);

    const applyGameEndFromServer = useCallback(
        (gameEndData) => {
            const { winner, loser, winnerStats, loserStats } = gameEndData;
            const oppName = props.gameData?.opponent?.name;
            const isDefeat = winner === oppName;
            const result = isDefeat ? "defeat" : "victory";
            const myStats = isDefeat ? loserStats : winnerStats;
            const message = isDefeat
                ? "Defeat! Train harder!"
                : "Victory! You have proven your mastery!";

            setWaitingForOpponentSwitch(false);
            setCurrentState({ move: result === "victory" ? "You won!" : "Match over" });

            const payload = {
                result,
                message,
                winner,
                loser,
                ratingChange: myStats.ratingChange,
                newRating: myStats.newRating,
                show: true
            };
            setGameEndResult(payload);
            setLastGameEndResult(payload);

            if (result === "victory") {
                console.log("🎉 VICTORY!");
            } else {
                console.log("💔 DEFEAT!");
            }

            const battleLog = document.createElement("div");
            battleLog.className = `battle-log-message ${result === "victory" ? "victory-message" : "defeat-message"}`;
            battleLog.innerHTML = `<span class="player-name">${winner}</span> won the battle!`;
            document.body.appendChild(battleLog);
            setTimeout(() => {
                if (document.contains(battleLog)) {
                    battleLog.remove();
                }
            }, 5000);

            if (props.setBattleLogs) {
                props.setBattleLogs((prevLogs) => {
                    const turnKeys = Object.keys(prevLogs || {});
                    const maxTurn = turnKeys.reduce((max, key) => {
                        const num = parseInt(key.replace("turn", ""), 10) || 0;
                        return num > max ? num : max;
                    }, 0);
                    const newTurnKey = `turn${maxTurn + 1}`;
                    return {
                        ...prevLogs,
                        [newTurnKey]: {
                            battleLogs: [
                                {
                                    title: result === "victory" ? "VICTORY!" : "DEFEAT!",
                                    healthChange: `${winner} won the battle!`,
                                    effectChange: [
                                        `New Rating: ${myStats.newRating} (${
                                            myStats.ratingChange >= 0 ? "+" : ""
                                        }${myStats.ratingChange})`
                                    ]
                                }
                            ]
                        }
                    };
                });
            }
        },
        [props.gameData, props.setBattleLogs]
    );

    /** After the last move animation clears, wait a beat before the result modal (readable GIF). */
    useEffect(() => {
        if (!stagedGameEnd) {
            return;
        }
        if (props.battleAnimationData?.actions?.length) {
            return;
        }
        const t = setTimeout(() => {
            applyGameEndFromServer(stagedGameEnd);
            setStagedGameEnd(null);
        }, 2400);
        return () => clearTimeout(t);
    }, [stagedGameEnd, props.battleAnimationData, applyGameEndFromServer]);

    const onMoveAnimationSequenceDone = useCallback(() => {
        if (props.setBattleAnimationData) {
            props.setBattleAnimationData(null);
        }
    }, [props.setBattleAnimationData]);

    console.log(userTeam, opponentTeam)
    const onClickCharecter = (charecter) => {
        // Reject any switch attempts after the match is already decided
        if (gameEnded) {
            console.log("Match is over - character switching is disabled");
            return;
        }

        // If a turn is in progress, don't allow switching
        if (isPaused) {
            console.log("Cannot switch character during turn execution");
            return;
        }

        // In Pokemon, switching happens immediately visually, but counts as your turn
        const idx = userTeam.findIndex(each => each.name === charecter.name);
        if (idx === -1) {
            console.error("Character not found in team:", charecter.name);
            return;
        }

        // Make sure the character is revealed
        userTeam[idx] = { ...charecter, isRevealed: true };

        // Clear switch required state and UI
        setNeedsSwitch(false);

        // Disable all UI interactions during switch
        setIsPaused(true);
        setCurrentState({ move: 'Switching character...' });

        // Disable character selection buttons
        const charButtons = document.querySelectorAll('.team-character-button');
        charButtons.forEach(btn => {
            btn.disabled = true;
            btn.classList.add('disabled-character');
        });

        // Disable move buttons
        const moveButtons = document.querySelectorAll('.move-button');
        moveButtons.forEach(btn => {
            btn.disabled = true;
        });

        // Emit character change event - in Pokemon, this triggers an immediate visual change
        // Check if this is a mandatory switch (character died) or voluntary
        const isMandatory = needsSwitch; // needsSwitch is set when character dies

        socket.emit("charecter_change", {
            charecter,
            userTeam,
            isMandatory: isMandatory
        }, (resp) => {
            console.log("Character change response:", resp);
        });
    }

    const onClickMove = (move) => {
        // Reject any move attempts after the match is already decided
        if (gameEnded) {
            console.log("Match is over - moves are disabled");
            return;
        }

        // Check if the character is defeated
        if (userSelectedCharecter.modifiedHealth <= 0) {
            console.log("Cannot use move with defeated character");

            // Show a Pokemon-style message to switch
            const battleLog = document.createElement('div');
            battleLog.className = 'battle-log-message';
            battleLog.innerHTML = `<span class="character-name">${userSelectedCharecter.name}</span> is unable to battle! <br> Please choose another character!`;
            document.body.appendChild(battleLog);

            // Remove the message after animation
            setTimeout(() => {
                if (document.contains(battleLog)) {
                    battleLog.remove();
                }
            }, 3000);

            return;
        }

        // If already in a paused state (turn in progress), don't allow another move
        if (isPaused) {
            console.log("Cannot use move during turn execution");
            return;
        }

        setIsPaused(true)
        setCurrentState({ move: 'Waiting for opponent...' })

        // Disable all move buttons to prevent multiple submissions
        const moveButtons = document.querySelectorAll('.move-button');
        moveButtons.forEach(btn => {
            btn.disabled = true;

            // Store original disabled state for buttons that should remain disabled
            if (btn.closest('[data-remaining="0"]')) {
                btn.setAttribute('data-should-be-disabled', 'true');
            }
        });

        // Disable character selection during move
        const charButtons = document.querySelectorAll('.team-character-button');
        charButtons.forEach(btn => {
            btn.disabled = true;
        });

        socket.emit("move_action", { userSelectedCharecter, move, opponentSelectedCharecter }, (resp) => {
            console.log("Move action response:", resp)
        })
    }

    // Debug logging for state changes
    useEffect(() => {
        console.log("userSelectedCharecter updated:", userSelectedCharecter?.name);
    }, [userSelectedCharecter]);

    useEffect(() => {
        console.log("opponentSelectedCharecter updated:", opponentSelectedCharecter?.name);
    }, [opponentSelectedCharecter]);

    // Add event listener for character switch required
    useEffect(() => {
        const handleNeedsSwitch = (event) => {
            console.log("Need to switch character:", event.detail);
            setNeedsSwitch(true);
        };

        document.addEventListener('needsCharacterSwitch', handleNeedsSwitch);

        return () => {
            document.removeEventListener('needsCharacterSwitch', handleNeedsSwitch);
        };
    }, []);

    useEffect(() => {
        function onCharecterChange(prop) {
            console.log("Character change event received:", prop);

            // Pokémon-style: When opponent switches, their new character is immediately shown
            if (prop.opponentTeam) {
                setOpponentTeam(prop.opponentTeam);
            } else {
                setOpponentTeam((prev) => {
                    if (!Array.isArray(prev) || !prop.charecter) {
                        return prev;
                    }
                    const next = [...prev];
                    const ix = findCharacterIndexInTeam(next, prop.charecter);
                    if (ix >= 0) {
                        next[ix] = markOpponentRevealed({ ...next[ix], ...prop.charecter });
                    }
                    return next;
                });
            }
            setOpponentSelectedCharceter(markOpponentRevealed(prop.charecter));

            // Clear the waiting indicator - opponent has chosen their next character
            setWaitingForOpponentSwitch(false);

            // Create Pokemon-style battle log message
            const battleLog = document.createElement('div');
            battleLog.className = 'battle-log-message opponent-message';
            battleLog.innerHTML = `<span class="player-name">Opponent</span> sent out <span class="character-name">${prop.charecter.name}</span>!`;
            document.body.appendChild(battleLog);

            // Remove the message after animation
            setTimeout(() => {
                if (document.contains(battleLog)) {
                    battleLog.remove();
                }
            }, 3000);

            // Show a visual notification in the game state
            setCurrentState({ move: `Opponent sent out ${prop.charecter.name}!` });

            // The battle system will handle re-enabling UI when both players have acted
        }

        // Handle confirmation of the player's own character switch
        function onCharacterSwitchConfirmed(props) {
            console.log("Character switch confirmed:", props);

            // Pokémon-style: When you switch, your new character is immediately shown to YOU
            // But the opponent won't see it until the turn is processed
            // Update the active character in state
            const previousCharacter = userSelectedCharecter;
            setUserSelectedCharecter(props.charecter);

            // Update the team state with the new character
            const userIndex = userTeam.findIndex(char => char._id === props.charecter._id);
            if (userIndex >= 0) {
                userTeam[userIndex] = props.charecter;
                setUserTeam([...userTeam]);
            }

            // Create Pokemon-style battle log message
            const battleLog = document.createElement('div');
            battleLog.className = 'battle-log-message';
            battleLog.innerHTML = `<span class="player-name">You</span> sent out <span class="character-name">${props.charecter.name}</span>!`;
            document.body.appendChild(battleLog);

            // Remove the message after animation
            setTimeout(() => {
                if (document.contains(battleLog)) {
                    battleLog.remove();
                }
            }, 3000);

            // Check if this is a mandatory switch (character died)
            if (props.isMandatory) {
                console.log("Mandatory switch - not submitting move action");
                // For mandatory switches, just update the UI and wait
                setCurrentState({ move: 'Select move' });
                setIsPaused(false); // Re-enable UI for mandatory switches

                // Re-enable character and move buttons
                const charButtons = document.querySelectorAll('.team-character-button');
                charButtons.forEach(btn => {
                    if (btn.getAttribute('data-defeated') !== 'true') {
                        btn.disabled = false;
                        btn.classList.remove('disabled-character');
                    }
                });

                const moveButtons = document.querySelectorAll('.move-button');
                moveButtons.forEach(btn => {
                    if (!btn.closest('[data-remaining="0"]')) {
                        btn.disabled = false;
                    }
                });

                return; // Don't submit move action for mandatory switches
            }

            // Clear the "Switching character..." message
            setCurrentState({ move: 'Waiting for opponent...' });

            // CRITICAL FIX: Automatically submit the switch action to complete the turn
            // In Pokemon, voluntary switching IS your turn action
            const switchActionMsg = {
                userSelectedCharecter: previousCharacter, // The character being switched out
                opponentSelectedCharecter: opponentSelectedCharecter,
                userTeam: props.userTeam || userTeam
            };

            console.log("Auto-submitting voluntary switch action:", switchActionMsg);
            socket.emit("move_action", switchActionMsg, (response) => {
                console.log("Switch action submitted:", response);
            });

            // Keep UI disabled until both players have acted
            // The battleLogs handler will re-enable the UI when the turn is complete
        }

        function moveEffects(value) {
            let { userSelectedCharecter, opponentSelectedCharecter, moveUsed, battleData } = value;

            console.log("Move Effects received:", battleData);

            // Check if this is a switch action (no moveUsed for switches)
            const isSwitch = !moveUsed || moveUsed.moveId === undefined;

            // Simple animation logic
            if (battleData) {
                const isFromOpponent = socket.id !== value.socketId;

                if (isSwitch) {
                    // Create a switch animation
                    // Animation handled by battle_animation_data event
                    // props.setBattleAnimationData removed to prevent overwrite

                    // If opponent switched, update the opponent's selected character
                    if (isFromOpponent && opponentSelectedCharecter) {
                        console.log("Updating opponent selected character from move effects:", opponentSelectedCharecter.name);
                        setOpponentSelectedCharceter(markOpponentRevealed({ ...opponentSelectedCharecter }));

                        // Update the UI to reflect the new active character
                        const opponentColumn = document.querySelector('.opponent-team-column');
                        if (opponentColumn) {
                            const opponentCharCards = opponentColumn.querySelectorAll('.character-card');
                            opponentCharCards.forEach(card => {
                                // Remove active class from all cards
                                card.classList.remove('active-character');
                                card.setAttribute('data-active', 'false');

                                // Add active class to the switched character
                                if (card.getAttribute('data-character-name') === opponentSelectedCharecter.name) {
                                    card.classList.add('active-character');
                                    card.setAttribute('data-active', 'true');
                                }
                            });
                        }
                    }
                }
            }

            // Update character data (robust index in case _id types differ)
            const oi = findCharacterIndexInTeam(opponentTeam, opponentSelectedCharecter);
            if (oi >= 0) {
                opponentTeam[oi] = markOpponentRevealed(opponentSelectedCharecter);
            }
            const ui = findCharacterIndexInTeam(userTeam, userSelectedCharecter);
            if (ui >= 0) {
                userTeam[ui] = userSelectedCharecter;
            }
            setOpponentTeam([...opponentTeam]);
            setUserTeam([...userTeam]);
            setUserSelectedCharecter({ ...userSelectedCharecter });
            setOpponentSelectedCharceter(markOpponentRevealed({ ...opponentSelectedCharecter }));

            // Update game state - enable UI except for completely depleted moves
            setIsPaused(false);

            // Re-enable character selection buttons
            const charButtons = document.querySelectorAll('.team-character-button');
            charButtons.forEach(btn => {
                if (btn.getAttribute('data-defeated') !== 'true') {
                    btn.disabled = false;
                    btn.classList.remove('disabled-character');
                }
            });

            const userDefeated = isCharacterDefeatedState(userSelectedCharecter);
            const oppDefeated = isCharacterDefeatedState(opponentSelectedCharecter);
            const opponentHasMoreChars = opponentTeam.some(char => {
                if (!char || sameCharacter(char, opponentSelectedCharecter)) {
                    return false;
                }
                return getCharacterCurrentHp(char) > 0;
            });
            const isVsAi = props.gameData?.opponent?.name === 'AI';

            // user_move_effects is emitted after battle_logs and was overwriting footer state.
            if (userDefeated) {
                const defeatMessage = document.createElement('div');
                defeatMessage.className = 'battle-log-message';
                defeatMessage.innerHTML = `<span class="character-name">${userSelectedCharecter.name}</span> is unable to battle!<br>Select another character!`;
                document.body.appendChild(defeatMessage);
                setTimeout(() => {
                    if (document.contains(defeatMessage)) {
                        defeatMessage.remove();
                    }
                }, 3000);
                setNeedsSwitch(true);
                setWaitingForOpponentSwitch(false);
                setCurrentState({ move: 'Switch character' });
            } else if (oppDefeated && opponentHasMoreChars) {
                if (isVsAi) {
                    setWaitingForOpponentSwitch(false);
                    setCurrentState({ move: 'Opponent is sending out their next character...' });
                } else {
                    setWaitingForOpponentSwitch(true);
                    setCurrentState({ move: 'Opponent is choosing their next character...' });
                }
            } else {
                setCurrentState({ move: 'Select move' });
            }

            // Re-enable move buttons unless you are down, out of uses, or waiting on a human switch
            const moveButtons = document.querySelectorAll('.move-button');
            const waitOnHumanOppSwitch =
                !isVsAi && oppDefeated && opponentHasMoreChars && !userDefeated;
            moveButtons.forEach(btn => {
                if (userDefeated || waitOnHumanOppSwitch) {
                    btn.disabled = true;
                } else if (!btn.closest('[data-remaining="0"]')) {
                    btn.disabled = false;
                }
            });
        }

        const battleLogs = (value) => {
            console.log("Battle logs received:", value);

            if (!value || !value.logs || Object.keys(value.logs).length === 0) {
                console.error("Invalid battle logs data received");
                return;
            }

            // Extract the logs from the most recent turn
            const currentTurn = Object.keys(value.logs).reduce((latest, key) => {
                return parseInt(key.replace('turn', '')) > parseInt(latest.replace('turn', '')) ? key : latest;
            }, Object.keys(value.logs)[0]);

            const logsData = value.logs[currentTurn];
            console.log("Using log data from turn:", currentTurn, logsData);

            // Set the battle logs for animation
            if (logsData) {
                // Ensure actions array is valid
                if (!logsData.actions || !Array.isArray(logsData.actions) || logsData.actions.length === 0) {
                    console.error("No valid actions array in log data");
                    return;
                }

                // Handle character changes from the logs
                if (logsData.characterChanges) {
                    const opponentId = Object.keys(logsData.characterChanges).find(id => id !== socket.id);

                    if (opponentId && logsData.characterChanges[opponentId] &&
                        logsData.characterChanges[opponentId].newCharacter) {

                        // Update opponent's character based on the switch
                        const newChar = markOpponentRevealed(
                            logsData.characterChanges[opponentId].newCharacter
                        );
                        console.log("Updating opponent's character from battle logs:", newChar.name);

                        setOpponentSelectedCharceter({ ...newChar });

                        // Update UI
                        const opponentColumn = document.querySelector('.opponent-team-column');
                        if (opponentColumn) {
                            const opponentCharCards = opponentColumn.querySelectorAll('.character-card');
                            opponentCharCards.forEach(card => {
                                // Remove active class from all cards
                                card.classList.remove('active-character');
                                card.setAttribute('data-active', 'false');

                                // Add active class to the switched character
                                if (card.getAttribute('data-character-name') === newChar.name) {
                                    card.classList.add('active-character');
                                    card.setAttribute('data-active', 'true');
                                }
                            });
                        }
                    }
                }

                // Set animation data directly from the actions array
                const animationData = {
                    actions: logsData.actions,
                    firstAttacker: logsData.firstAttacker,
                    isCompleted: logsData.isCompleted,
                    missedMoves: logsData.missedMoves || []
                };

                console.log("Setting battle animation data:", animationData);
                props.setBattleAnimationData(animationData);

                // Accumulate logs for chat display (merge with previous turns)
                props.setBattleLogs(prevLogs => ({
                    ...(prevLogs && typeof prevLogs === 'object' && !Array.isArray(prevLogs) ? prevLogs : {}),
                    ...value.logs
                }));

                // Update character states from battle log results (any number of actions:
                // e.g. one action when the AI KOs you before your move, or two when both act).
                if (logsData.actions && logsData.actions.length > 0) {
                    logsData.actions.forEach(action => {
                        if (!action.msg) return;
                        if (action.socketId === socket.id) {
                            if (action.msg.userSelectedCharecter) {
                                const updatedChar = action.msg.userSelectedCharecter;
                                const userIndex = findCharacterIndexInTeam(userTeam, updatedChar);
                                if (userIndex >= 0) {
                                    userTeam[userIndex] = updatedChar;
                                    setUserTeam([...userTeam]);
                                }
                                setUserSelectedCharecter(updatedChar);
                            }
                            if (action.msg.opponentSelectedCharecter) {
                                const updatedOppChar = markOpponentRevealed(action.msg.opponentSelectedCharecter);
                                const oppIndex = findCharacterIndexInTeam(opponentTeam, updatedOppChar);
                                if (oppIndex >= 0) {
                                    opponentTeam[oppIndex] = updatedOppChar;
                                    setOpponentTeam([...opponentTeam]);
                                }
                                setOpponentSelectedCharceter(updatedOppChar);
                            }
                        } else {
                            if (action.msg.userSelectedCharecter) {
                                const updatedOppChar = markOpponentRevealed(action.msg.userSelectedCharecter);
                                const oppIndex = findCharacterIndexInTeam(opponentTeam, updatedOppChar);
                                if (oppIndex >= 0) {
                                    opponentTeam[oppIndex] = updatedOppChar;
                                    setOpponentTeam([...opponentTeam]);
                                }
                                setOpponentSelectedCharceter(updatedOppChar);
                            }
                            if (action.msg.opponentSelectedCharecter) {
                                const hitChar = action.msg.opponentSelectedCharecter;
                                const userIndex = findCharacterIndexInTeam(userTeam, hitChar);
                                if (userIndex >= 0) {
                                    userTeam[userIndex] = hitChar;
                                    setUserTeam([...userTeam]);
                                }
                                // Always sync active defender from the server snapshot (IDs can miss the team row).
                                setUserSelectedCharecter(hitChar);
                            }
                        }
                    });
                }

                // Update game state based on user's current character health
                let currentUserCharacter = userSelectedCharecter;

                if (logsData.characterChanges && logsData.characterChanges[socket.id]) {
                    currentUserCharacter = logsData.characterChanges[socket.id].newCharacter;
                    setUserSelectedCharecter(currentUserCharacter);
                } else {
                    const userAction = logsData.actions.find(a => a.socketId === socket.id);
                    if (userAction && userAction.msg && userAction.msg.userSelectedCharecter) {
                        currentUserCharacter = userAction.msg.userSelectedCharecter;
                        setUserSelectedCharecter(currentUserCharacter);
                    } else {
                        // You did not get a "your" action this turn (e.g. only the AI moved) —
                        // your Pokémon is msg.opponentSelectedCharecter on their move event.
                        const theirMove = logsData.actions.find(
                            a =>
                                a.socketId !== socket.id &&
                                a.msg &&
                                (a.type === 'move' || a.msg.move)
                        );
                        if (theirMove && theirMove.msg.opponentSelectedCharecter) {
                            const def = theirMove.msg.opponentSelectedCharecter;
                            const onYourTeam = userTeam.some(c => sameCharacter(c, def));
                            if (onYourTeam) {
                                currentUserCharacter = def;
                                setUserSelectedCharecter(def);
                            }
                        }
                    }
                }

                // Reset game state after turn processing
                setIsPaused(false);

                // Re-enable character selection (defeated stay disabled)
                const charButtons = document.querySelectorAll('.team-character-button');
                charButtons.forEach(btn => {
                    if (btn.getAttribute('data-defeated') !== 'true') {
                        btn.disabled = false;
                        btn.classList.remove('disabled-character');
                    }
                });

                // Determine the latest opponent character state from the logs.
                // IMPORTANT: when the user's attack kills the opponent, the server
                // skips the opponent's action for this turn, so we can't rely on
                // finding an action with a different socketId. The most reliable
                // source is our OWN action's msg.opponentSelectedCharecter.
                let currentOpponentCharacter = opponentSelectedCharecter;
                const myAction = logsData.actions.find(a => a.socketId === socket.id);
                if (myAction && myAction.msg && myAction.msg.opponentSelectedCharecter) {
                    currentOpponentCharacter = myAction.msg.opponentSelectedCharecter;
                } else {
                    const oppAction = logsData.actions.find(a => a.socketId !== socket.id);
                    if (oppAction && oppAction.msg && oppAction.msg.userSelectedCharecter) {
                        currentOpponentCharacter = oppAction.msg.userSelectedCharecter;
                    }
                }

                const opponentIsDefeated =
                    currentOpponentCharacter && isCharacterDefeatedState(currentOpponentCharacter);
                const userIsDefeated =
                    currentUserCharacter && isCharacterDefeatedState(currentUserCharacter);

                const opponentHasMoreChars = opponentTeam.some(char => {
                    if (!char || sameCharacter(char, currentOpponentCharacter)) {
                        return false;
                    }
                    return getCharacterCurrentHp(char) > 0;
                });

                console.log('[WAITING CHECK]', {
                    opponentIsDefeated,
                    userIsDefeated,
                    opponentHasMoreChars,
                    currentOpponentName: currentOpponentCharacter?.name,
                    currentOpponentHealth: currentOpponentCharacter?.modifiedHealth,
                });

                const isVsAi = props.gameData?.opponent?.name === 'AI';

                if (opponentIsDefeated && opponentHasMoreChars && !userIsDefeated && !isVsAi) {
                    // Human opponent must pick their next character — block input until they do
                    setWaitingForOpponentSwitch(true);
                    setCurrentState({ move: 'Opponent is choosing their next character...' });
                } else if (opponentIsDefeated && opponentHasMoreChars && !userIsDefeated && isVsAi) {
                    // AI switches on the server; do not lock the UI waiting for a human
                    setWaitingForOpponentSwitch(false);
                    setCurrentState({ move: 'Opponent is sending out their next character...' });
                } else if (userIsDefeated) {
                    setWaitingForOpponentSwitch(false);
                    setNeedsSwitch(true);
                    setCurrentState({ move: 'Switch character' });
                } else {
                    setCurrentState({ move: 'Select move' });
                }

                const moveButtons = document.querySelectorAll('.move-button');
                const waitOnHumanOppSwitchBl =
                    !isVsAi && opponentIsDefeated && opponentHasMoreChars && !userIsDefeated;
                moveButtons.forEach(btn => {
                    if (userIsDefeated || waitOnHumanOppSwitchBl) {
                        btn.disabled = true;
                    } else if (!btn.closest('[data-remaining="0"]')) {
                        btn.disabled = false;
                    }
                });
            }
        }

        const battleAnimationData = (value) => {
            console.log("Battle animation data received:", value);

            if (!value || !value.actions || value.actions.length === 0) {
                console.error("Invalid battle animation data received");
                return;
            }

            // Process the ordered actions and create proper animation data
            const animationData = {
                actions: value.actions.map(action => {
                    if (action.isSwitch) {
                        // For switches, we need to get the character data from characterChanges
                        const characterChange = value.characterChanges && value.characterChanges[action.socketId];
                        const switchCharacter = characterChange ? characterChange.newCharacter : action.msg.switchTo;

                        return {
                            socketId: action.socketId,
                            msg: {
                                userSelectedCharecter: switchCharacter,
                                switchTo: switchCharacter
                            },
                            isSwitch: true
                        };
                    } else {
                        const d = action.damage;
                        const damageNum =
                            typeof d === 'number' && !Number.isNaN(d)
                                ? d
                                : d != null && d !== '' && Number.isFinite(Number(d))
                                    ? Number(d)
                                    : undefined;
                        return {
                            socketId: action.socketId,
                            msg: action.msg,
                            isSwitch: false,
                            isMissed: action.missed === true, // Use specific event property
                            ...(damageNum !== undefined ? { damage: damageNum } : {}),
                            hasDied: value.defeatedBeforeMove && value.defeatedBeforeMove.includes(action.msg.opponentSelectedCharecter?.name)
                        };
                    }
                }),
                isCompleted: true,
                turnNumber: value.turnNumber
            };

            console.log("Setting ordered battle animation data:", animationData);
            props.setBattleAnimationData(animationData);
        }

        socket.on("opponent_charecter_change", onCharecterChange);
        socket.on("character_switch_confirmed", onCharacterSwitchConfirmed);
        socket.on("user_move_effects", moveEffects);
        socket.on("opponent_move_effects", moveEffects);
        socket.on("battle_logs", battleLogs);
        socket.on("battle_animation_data", battleAnimationData);

        // Handle team status check for game end detection
        socket.on("check_team_status", () => {
            const allDefeated = userTeam.every(char => char.modifiedHealth <= 0);
            const remainingCharacters = userTeam.filter(char => char.modifiedHealth > 0).length;

            socket.emit("team_status_report", {
                allDefeated,
                remainingCharacters
            });
        });

        socket.on("game_end", (gameEndData) => {
            console.log("Game End Event Received:", gameEndData);
            setGameEnded(true);
            setIsPaused(true);
            setWaitingForOpponentSwitch(false);
            setCurrentState({ move: "Match ending..." });
            setStagedGameEnd(gameEndData);
        });

        return () => {
            socket.off('opponent_charecter_change', onCharecterChange);
            socket.off('character_switch_confirmed', onCharacterSwitchConfirmed);
            socket.off('user_move_effects', moveEffects);
            socket.off('opponent_move_effects', moveEffects);
            socket.off('battle_logs', battleLogs);
            socket.off('check_team_status');
            socket.off('game_end');
        };
    }, [opponentTeam, userTeam, props, needsSwitch, userSelectedCharecter, opponentSelectedCharecter]);

    // DEV ONLY: react to app-level preview-modal shortcut
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') return;
        if (props.devPreviewResult) {
            setGameEndResult(props.devPreviewResult);
        } else if (gameEndResult && gameEndResult._devPreview) {
            setGameEndResult(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.devPreviewResult]);

    // Game end modal handlers
    const handleCloseGameEndModal = () => {
        // Keep `gameEnded` true so the board stays locked, but hide the modal
        // so the player can review the final board state. They can get back
        // to the home screen with the persistent "Back to Home" footer button.
        setGameEndResult(null);
    };

    const handleBackToHome = () => {
        // Full reload drops all match state cleanly and returns to the lobby
        window.location.href = '/';
    };

    const handleGoToLeaderboard = () => {
        window.location.href = '/leaderboard';
    };

    const handleForfeit = () => {
        if (gameEnded) return;
        setConfirmingForfeit(true);
    };

    const confirmForfeit = () => {
        if (gameEnded) return;
        setConfirmingForfeit(false);
        // Optimistically lock the board so the user can't spam more actions
        // while the server processes the forfeit.
        setIsPaused(true);
        setCurrentState({ move: 'Forfeiting match...' });
        if (props.setBattleAnimationData) {
            props.setBattleAnimationData(null);
        }
        socket.emit('forfeit', {}, (ack) => {
            if (ack && ack.error) {
                console.error('Forfeit failed:', ack.error);
                setCurrentState({ move: 'Select move' });
                setIsPaused(false);
            }
            // On success, the server will broadcast `game_end` which handles
            // locking the UI and showing the modal (with the opponent winning).
        });
    };

    const cancelForfeit = () => setConfirmingForfeit(false);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
        >
            <GameContainer>
                <Row className="h-100">
                    <TeamColumn md='2'>
                        <motion.div
                            initial={{ x: -50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.2, duration: 0.5 }}
                            className="user-team-column"
                        >
                            <TeamLayout
                                team={userTeam}
                                selectedCharacter={userSelectedCharecter}
                                onClickCharecter={onClickCharecter}
                                type='user'
                            />
                        </motion.div>
                    </TeamColumn>
                    <BattleColumn md='8'>
                        <BattleLayout
                            user={userSelectedCharecter}
                            opp={opponentSelectedCharecter}
                            onClickMove={onClickMove}
                            battleAnimationData={props.battleAnimationData}
                            battleLogs={props.battleLogs}
                            waitingForOpponentSwitch={waitingForOpponentSwitch}
                            gameEnded={gameEnded}
                            setBattleAnimationData={props.setBattleAnimationData}
                            onMoveAnimationSequenceDone={onMoveAnimationSequenceDone}
                            yourTrainerName={props.username}
                            opponentTrainerName={props.gameData?.opponent?.name || 'Opponent'}
                            viewerSocketId={socket.id}
                        />
                    </BattleColumn>
                    <TeamColumn md='2'>
                        <motion.div
                            initial={{ x: 50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.2, duration: 0.5 }}
                            className="opponent-team-column"
                        >
                            <TeamLayout
                                team={opponentTeam}
                                selectedCharacter={opponentSelectedCharecter}
                                type='opponent'
                            />
                        </motion.div>
                    </TeamColumn>
                </Row>
            </GameContainer>

            <GameFooter>
                <StatusMessage>
                    {/* {currentState.move === 'Switch character' && (
                        <>
                            <FaSkull color="#ff5252" size={16} />
                            <span className="defeated-message" style={{ 
                                color: "#ff5252", 
                                fontWeight: "700",
                                animation: "pulse 1.5s infinite"
                            }}>Switch character</span>
                        </>
                    )} */}
                    {currentState.move === 'Select move' && <FaFistRaised color="#4caf50" size={16} />}
                    <span>{currentState.move}</span>
                </StatusMessage>

                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    {gameEnded ? (
                        <>
                            {!gameEndResult && lastGameEndResult && (
                                <ReopenResultButton onClick={() => setGameEndResult(lastGameEndResult)}>
                                    Show Results
                                </ReopenResultButton>
                            )}
                            <HomeButton onClick={handleBackToHome} title="Return to the home screen">
                                Back to Home
                            </HomeButton>
                        </>
                    ) : (
                        confirmingForfeit ? (
                            <ForfeitConfirmGroup>
                                <span>Forfeit and give opponent the win?</span>
                                <button className="confirm-yes" onClick={confirmForfeit}>Yes, forfeit</button>
                                <button className="confirm-no" onClick={cancelForfeit}>Cancel</button>
                            </ForfeitConfirmGroup>
                        ) : (
                            <ForfeitButton onClick={handleForfeit} disabled={isPaused} title="Quit the match (opponent wins)">
                                Forfeit
                            </ForfeitButton>
                        )
                    )}
                    <Countdown isPaused={isPaused || gameEnded} />
                </div>
            </GameFooter>

            {/* Game End Modal with Confetti */}
            {gameEndResult && gameEndResult.show && (
                <>
                    {gameEndResult.result === "victory" && (
                        <Confetti
                            width={window.innerWidth}
                            height={window.innerHeight}
                            recycle={false}
                            numberOfPieces={300}
                            gravity={0.15}
                            style={{ position: 'fixed', top: 0, left: 0, zIndex: 10001 }}
                        />
                    )}

                    <AnimatePresence>
                        <GameEndOverlay
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <GameEndModal
                                $victory={gameEndResult.result === "victory"}
                                initial={{ scale: 0.8, y: 40, opacity: 0 }}
                                animate={{ scale: 1, y: 0, opacity: 1 }}
                                exit={{ scale: 0.8, y: 40, opacity: 0 }}
                                transition={{ type: "spring", damping: 20, stiffness: 120 }}
                            >
                                <ModalHeader $victory={gameEndResult.result === "victory"}>
                                    <GameEndIcon
                                        $victory={gameEndResult.result === "victory"}
                                        initial={{ scale: 0, rotate: -20 }}
                                        animate={{ scale: 1, rotate: 0 }}
                                        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                                    >
                                        {gameEndResult.result === "victory" ? "🏆" : "⚔️"}
                                    </GameEndIcon>
                                    <GameEndTitle
                                        $victory={gameEndResult.result === "victory"}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.3 }}
                                    >
                                        {gameEndResult.result === "victory" ? "VICTORY" : "DEFEAT"}
                                    </GameEndTitle>
                                    <GameEndSubtitle
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.4 }}
                                    >
                                        {gameEndResult.message}
                                    </GameEndSubtitle>
                                </ModalHeader>

                                <ModalBody>
                                    <ResultCard
                                        initial={{ opacity: 0, y: 15 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.5 }}
                                    >
                                        <ResultRow>
                                            <ResultLabel>Winner</ResultLabel>
                                            <ResultValue $highlight>{gameEndResult.winner}</ResultValue>
                                        </ResultRow>
                                        <ResultRow>
                                            <ResultLabel>Defeated</ResultLabel>
                                            <ResultValue>{gameEndResult.loser}</ResultValue>
                                        </ResultRow>
                                    </ResultCard>

                                    {gameEndResult.ratingChange !== undefined && (
                                        <RatingSection
                                            $positive={gameEndResult.ratingChange >= 0}
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ delay: 0.7, type: "spring" }}
                                        >
                                            <RatingNumber>
                                                {gameEndResult.newRating}
                                                <small>Your Rating</small>
                                            </RatingNumber>
                                            <RatingDelta
                                                $positive={gameEndResult.ratingChange >= 0}
                                                initial={{ x: -10, opacity: 0 }}
                                                animate={{ x: 0, opacity: 1 }}
                                                transition={{ delay: 0.9 }}
                                            >
                                                {gameEndResult.ratingChange >= 0 ? '▲' : '▼'}
                                                {gameEndResult.ratingChange >= 0 ? '+' : ''}{gameEndResult.ratingChange}
                                            </RatingDelta>
                                        </RatingSection>
                                    )}
                                </ModalBody>

                                <ModalFooter>
                                    <PrimaryButton
                                        $victory={gameEndResult.result === "victory"}
                                        onClick={handleBackToHome}
                                        whileHover={{ scale: 1.03 }}
                                        whileTap={{ scale: 0.97 }}
                                    >
                                        Back to Home
                                    </PrimaryButton>
                                    <SecondaryButton
                                        onClick={handleGoToLeaderboard}
                                        whileHover={{ scale: 1.03 }}
                                        whileTap={{ scale: 0.97 }}
                                    >
                                        Leaderboard
                                    </SecondaryButton>
                                    <SecondaryButton
                                        onClick={handleCloseGameEndModal}
                                        whileHover={{ scale: 1.03 }}
                                        whileTap={{ scale: 0.97 }}
                                        title="Review the final board (you can come back via 'Show Results')"
                                    >
                                        Review Board
                                    </SecondaryButton>
                                </ModalFooter>
                            </GameEndModal>
                        </GameEndOverlay>
                    </AnimatePresence>
                </>
            )}
        </motion.div>
    )
}


export default GameLayout;
